/**
 * Community callout card — surfaces the Slack invite at the top of the
 * Members Directory. Two-state:
 *
 *   • Not joined → pink/sky gradient callout, "Join our Slack community"
 *     with a Slack-branded Join button. Clicking flips the joined flag
 *     and opens the invite link.
 *   • Joined → same layout, quieter tone (no accent gradient), "Open
 *     Slack workspace" button that deep-links to the workspace + a
 *     small "Not joined yet? Reset" undo affordance in case the flag
 *     got flipped by mistake.
 */
import React from "react";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import {
  SlackMark,
} from "./CommunityButton";
import {
  slackDestination,
  useSlackJoined,
  markSlackJoined,
  resetSlackJoined,
  SLACK_INVITE_URL,
  SLACK_WORKSPACE_URL,
} from "../lib/slack";

export default function SlackJoinCard() {
  const [joined] = useSlackJoined();
  const href = slackDestination(joined);
  if (!href) return null; // env vars missing → hide silently

  return (
    <div
      data-testid="members-slack-join"
      className={
        "mt-6 rounded-2xl border overflow-hidden " +
        (joined
          ? "border-shell-border bg-white"
          : "border-transparent bg-gradient-to-br from-brand-pink/10 via-white to-sky-500/10 ring-1 ring-brand-pink/30")
      }
    >
      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white border border-shell-border flex items-center justify-center shadow-sm">
          <SlackMark className="w-6 h-6 md:w-7 md:h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-brand-pink">
            {joined ? "You're in" : "Community"}
          </div>
          <div className="mt-1 font-semibold text-foreground text-base md:text-lg">
            {joined
              ? "Jump back into the CPG Institute Slack"
              : "Join the CPG Institute Slack community"}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {joined
              ? "Continue the conversation with founders inside our private workspace."
              : "Trade insights, wins, and warm intros with founders inside our private Slack workspace."}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {joined ? (
            <>
              <a
                href={SLACK_WORKSPACE_URL || href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="members-slack-open"
                className="btn-primary"
              >
                Open Slack <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => resetSlackJoined()}
                data-testid="members-slack-reset"
                title="Not joined yet? Reset"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </>
          ) : (
            <a
              href={SLACK_INVITE_URL || href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => markSlackJoined()}
              data-testid="members-slack-join-btn"
              className="btn-primary"
            >
              Join our Slack <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
